import { useMemo } from 'react';
import DOMPurify from 'dompurify';
import { useNavigate } from 'js/hooks/navigate';
import { getScript, getStylesheet } from 'js/utils/scripts.util';
import { useShowNotification } from 'js/hooks/useShowNotification';
import { useIsAllowedToRedirect } from 'js/hooks/useIsAllowedToRedirect';

export const useCommonPublicAPI = () => {
    const navigate = useNavigate();
    const showNotification = useShowNotification();
    const replace = useNavigate({ replace: true });
    const redirect = useNavigate({ redirect: true });
    const isAllowedToRedirect = useIsAllowedToRedirect();

    return useMemo(() => ({
        /**
         * Display toast notification
         * @memberOf Playtech.API.common
         * @function
         * @param {string} messageOrTKey Translation Key or message to be shown in toast notification, message can be HTML text.
         * @param {Object} [toastOptions] Notistack toast message options. See [docs]{@link https://notistack.com/api-reference#enqueuesnackbar-options}
         * @param {('default' | 'info' | 'success' | 'warning'  | 'error')} [toastOptions.variant = 'default']
         * @param {number} [toastOptions.autoHideDuration] Toast display duration in seconds. Default value '5' if custom value is not set in 'Toast Message Preferences' component.
         * @example
         * Playtech.API.common.showNotification('playtech.mobile.system.error.default');
         * Playtech.API.common.showNotification('playtech.mobile.system.error.default', { variant: 'error' });
         * Playtech.API.common.showNotification('Success!!!', { autoHideDuration: 5, variant: 'success' });
         * Playtech.API.common.showNotification('This is <b>NOT</b> allowed!', { variant: 'warning' });         *
         * Playtech.API.common.showNotification('playtech.mobile.system.error.default', {
         *     SnackbarProps: {'aria-label': 'text'},
         *     className: 'className',
         *     onClose: () => console.log('snack was closed')
         * });
         */
        showNotification,
        /**
         * Sets global toast notifications options
         * @memberOf Playtech.API.common
         * @function
         * @param {Object} options Global Notistack toast messages options to override default Portal options. See [docs]{@link https://notistack.com/api-reference#enqueuesnackbar-options}
         * @param {number} [options.autoHideDuration] Toast display duration in seconds. Default value '5' if custom value is not set in 'Toast Message Preferences' component.
         * @example
         * Playtech.API.common.setNotificationsOptions({ hideIconVariant: false, disableWindowBlurListener: true, autoHideDuration: 5 });
         */
        setNotificationsOptions: (options) => window.Playtech.API.common.notificationsOptions = options,
        /**
         * Navigates to another page.
         * @memberOf Playtech.API.common
         * @function
         * @param {string} path Relative path of the existing page to redirect to.
         * @param {Object} [state] State object to store with the new navigate.
         * @param {boolean} [state.fromPopup]
         * @param {Object} [options]
         * @param {string[]} [options.removeParams=[]] List of query string parameters to remove upon navigation.
         * @param {boolean} [options.preserveSearchParams=true] Preserve existing query string parameters.
         * @example
         * Playtech.API.common.navigate(
         *        'test-page',
         *        { fromPopup: false },
         *        { removeParams: ['test'], preserveSearchParams: true }
         * );
         */
        navigate,
        /**
         * Navigates to another page but replaces current history frame.
         * @memberOf Playtech.API.common
         * @function
         * @param {string} path Relative path of the existing page to redirect to.
         * @param {Object} [state] State object to store with the new navigate.
         * @param {Object} [options]
         * @param {string[]} [options.removeParams=[]] List of query string parameters to remove upon navigation.
         * @param {boolean} [options.preserveSearchParams=true] Preserve existing query string parameters.
         * @param {boolean} [options.preserveHash=false] Preserve existing query string parameters.
         * @example
         * Playtech.API.common.replace(
         *        'test-page',
         *        { fromPopup: false },
         *        { removeParams: ['test'], preserveSearchParams: true }
         * );
         */
        replace,
        /**
         * Redirects to another page (full reload).
         * @memberOf Playtech.API.common
         * @function
         * @param {string} path Relative path of the existing page to redirect to.
         * @param {Object} [state] State object to store with the new navigate.
         * @param {Object} [options]
         * @param {string[]} [options.removeParams=[]] List of query string parameters to remove upon navigation.
         * @param {boolean} [options.preserveSearchParams=true] Preserve existing query string parameters.
         * @param {boolean} [options.preserveHash=false] Preserve existing query string parameters.
         * @example
         * Playtech.API.common.redirect(
         *        'test-page',
         *       { fromPopup: false },
         *       {
         *            removeParams: ['test'],
         *            preserveSearchParams: true
         *        }
         *);
         */
        redirect,
        /**
         * Return whether the Portal can be redirected to page according to allowedRedirectHostNames.
         * @memberOf Playtech.API.common
         * @function
         * @param {string} path Relative path or full link of the existing page to redirect to.
         * @returns {boolean}
         * @example
         * Playtech.API.common.isAllowedToRedirect('/deposit');
         * Playtech.API.common.isAllowedToRedirect('https://google.com');
         */
        isAllowedToRedirect: (path) => isAllowedToRedirect(path),
        /**
         * Load script.
         * @memberOf Playtech.API.common
         * @function
         * @param {string} url URL to JS file to load.
         * @param {Object} [options] options for loading JS file.
         * @param {string} [options.parent = 'head'] Tag in witch script should be mounted, usage: <code>document.getElementsByTagName(parent)[0].appendChild(script)<code>.
         * @param {string} [options.id] HTML id attribute of created script.
         * @param {string} [options.type = 'text/javascript'] HTML type attribute of created script.
         * @returns {Promise<{Event}>} 'load' type event
         * @example
         * Playtech.API.common.getScript('https://www.example.com', { parent: 'head', id: 'container', type: 'module' }).then((event) => { ... });
         */
        getScript,
        /**
         * Load styles.
         * @memberOf Playtech.API.common
         * @function
         * @param {string} url URL to CSS file to load.
         * @returns {Promise<{Event}>} 'load' type event
         * @example
         * Playtech.API.common.getStylesheet('https://www.example.com').then((event) => { ... });
         */
        getStylesheet,
        /**
         * Sanitize HTML string.
         * @memberOf Playtech.API.common
         * @function
         * @param {string} html HTML string to sanitize.
         * @returns {string} Sanitized HTML string.
         * @example
         * Playtech.API.common.sanitize('<script>alert("XSS")</script>');
         */
        sanitize: DOMPurify.sanitize
    }), [showNotification, navigate, redirect, replace, isAllowedToRedirect]);
};
